/**
 * AccountCreate.js
 * 
 * Raider Depot — Optional Adult Account Creation
 * Route: /account
 * 
 * Per workflow doc section 6 (Client Registration Flow — OPTIONAL):
 *   1. Adult enters email + password
 *   2. Adult enters their household token (from QR card)
 *   3. Firebase Email/Password account created
 *   4. Token linked to account in Firestore `accounts` collection
 *   5. Email verification sent
 *   6. Redirect to /order (token auto-populated)
 * 
 * Privacy: Only the email is collected. No name, no address, no PII
 * beyond the email itself. The email is used solely for:
 *   - Login persistence across devices
 *   - Order confirmation emails (Agent 6)
 *   - Cycle-open notifications (Agent 6)
 */

import React, { useState } from "react";
import { auth } from "../firebase";
import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
} from "firebase/auth";
import NavBar from "../components/NavBar";

// ── Constants ───────────────────────────────────────────────────────
const HOUSEHOLD_API_URL = "/api/households";
const ACCOUNT_API_URL = "/api/accounts";
const TOKEN_STORAGE_KEY = "raider-depot-token";

// ── Styles (consistent with existing Raider Depot UI) ───────────────
const styles = {
  container: {
    minHeight: "100vh",
    backgroundColor: "#f0f4f8",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "24px 16px",
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  card: {
    backgroundColor: "#ffffff",
    borderRadius: "16px",
    boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
    padding: "32px 28px",
    maxWidth: "480px",
    width: "100%",
    marginBottom: "20px",
  },
  title: {
    fontSize: "26px",
    fontWeight: "700",
    color: "#1a365d",
    textAlign: "center",
    margin: "0 0 4px 0",
  },
  subtitle: {
    fontSize: "14px",
    color: "#64748b",
    textAlign: "center",
    margin: "0 0 24px 0",
    lineHeight: "1.5",
  },
  privacyBanner: {
    backgroundColor: "#ebf5ff",
    borderRadius: "10px",
    padding: "12px 16px",
    textAlign: "center",
    fontSize: "13px",
    color: "#2563eb",
    marginBottom: "20px",
    lineHeight: "1.5",
  },
  label: {
    display: "block",
    fontSize: "13px",
    fontWeight: "600",
    color: "#475569",
    marginBottom: "6px",
    marginTop: "16px",
  },
  input: {
    width: "100%",
    padding: "12px 14px",
    fontSize: "15px",
    border: "2px solid #e2e8f0",
    borderRadius: "10px",
    outline: "none",
    boxSizing: "border-box",
    transition: "border-color 0.2s",
  },
  tokenInput: {
    width: "100%",
    padding: "12px 14px",
    fontSize: "16px",
    fontWeight: "600",
    textAlign: "center",
    border: "2px solid #e2e8f0",
    borderRadius: "10px",
    outline: "none",
    letterSpacing: "2px",
    textTransform: "uppercase",
    boxSizing: "border-box",
    transition: "border-color 0.2s",
  },
  buttonPrimary: {
    width: "100%",
    padding: "14px",
    fontSize: "16px",
    fontWeight: "600",
    color: "#ffffff",
    backgroundColor: "#3b82f6",
    border: "none",
    borderRadius: "10px",
    cursor: "pointer",
    transition: "background-color 0.2s",
    marginTop: "24px",
  },
  buttonDisabled: {
    backgroundColor: "#94a3b8",
    cursor: "not-allowed",
  },
  linkButton: {
    background: "none",
    border: "none",
    color: "#3b82f6",
    fontSize: "14px",
    cursor: "pointer",
    textDecoration: "underline",
    padding: 0,
    marginTop: "16px",
  },
  errorText: {
    color: "#ef4444",
    fontSize: "13px",
    marginTop: "8px",
  },
  successCard: {
    textAlign: "center",
  },
  successIcon: {
    fontSize: "48px",
    marginBottom: "12px",
  },
  stepIndicator: {
    display: "flex",
    justifyContent: "center",
    gap: "8px",
    marginBottom: "24px",
  },
  stepDot: {
    width: "10px",
    height: "10px",
    borderRadius: "50%",
    backgroundColor: "#e2e8f0",
  },
  stepDotActive: {
    backgroundColor: "#3b82f6",
  },
  infoText: {
    fontSize: "12px",
    color: "#94a3b8",
    textAlign: "center",
    marginTop: "12px",
    lineHeight: "1.5",
  },
  passwordReqs: {
    fontSize: "12px",
    color: "#94a3b8",
    marginTop: "6px",
    lineHeight: "1.5",
  },
};

// ── Component ───────────────────────────────────────────────────────
export default function AccountCreate() {
  const [step, setStep] = useState(1); // 1: token, 2: email/password, 3: success
  const [token, setToken] = useState("");
  // eslint-disable-next-line no-unused-vars
  const [_tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [householdSize, setHouseholdSize] = useState("");
  const [adultAttestation, setAdultAttestation] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // ── Step 1: Validate Token ────────────────────────────────────────
  const validateToken = async () => {
    if (!token || token.length < 6) {
      setTokenError("Please enter your Raider Depot token (e.g. RD-RW-001)");
      return;
    }

    setLoading(true);
    setTokenError("");

    try {
      const res = await fetch(`${HOUSEHOLD_API_URL}/${token}`);
      if (res.ok) {
        const data = await res.json();
        if (data.status === "active") {
          setTokenValid(true);
          setStep(2);
        } else {
          setTokenError("This token is not active. Please contact your school counselor.");
        }
      } else if (res.status === 404) {
        setTokenError("Token not found. Please check your token and try again.");
      } else {
        setTokenError("Something went wrong. Please try again.");
      }
    } catch (err) {
      setTokenError("Unable to connect. Please check your internet and try again.");
    } finally {
      setLoading(false);
    }
  };

  // ── Step 2: Create Account ────────────────────────────────────────
  const createAccount = async () => {
    setError("");

    // Validate email
    if (!email || !email.includes("@")) {
      setError("Please enter a valid email address.");
      return;
    }

    // Validate password
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    if (!householdSize) {
      setError("Please select your household size.");
      return;
    }

    if (!adultAttestation) {
      setError("You must confirm that you are 18 or older to create an account.");
      return;
    }

    setLoading(true);

    try {
      // 1. Create Firebase Auth account
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      const user = cred.user;

      // 2. Send email verification
      await sendEmailVerification(user);

      // 3. Link token to account via API
      //    This creates the `accounts` doc in Firestore
      const idToken = await user.getIdToken();
      const linkRes = await fetch(ACCOUNT_API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          tokenId: token,
          householdSize: parseInt(householdSize, 10),
          adultAttestation: true,
          attestedAt: new Date().toISOString(),
        }),
      });

      if (!linkRes.ok) {
        const errData = await linkRes.json().catch(() => ({}));
        // Account created but link failed — still show success
        // but warn about token link
        console.error("Token link failed:", errData);
      }

      // 4. Save token to localStorage for PWA
      try {
        localStorage.setItem(TOKEN_STORAGE_KEY, token);
      } catch (e) {
        // no-op
      }

      setStep(3);
    } catch (err) {
      if (err.code === "auth/email-already-in-use") {
        setError(
          "An account with this email already exists. Please sign in instead."
        );
      } else if (err.code === "auth/weak-password") {
        setError("Password is too weak. Please use at least 8 characters.");
      } else if (err.code === "auth/invalid-email") {
        setError("Please enter a valid email address.");
      } else {
        setError("Account creation failed. Please try again.");
        console.error("Account create error:", err);
      }
    } finally {
      setLoading(false);
    }
  };

  // ── Render: Step 1 — Token Entry ──────────────────────────────────
  if (step === 1) {
    return (
      <div style={styles.container}>
        <NavBar title="Create Account" />
        <div style={styles.card}>
          <div style={{ textAlign: "center", fontSize: "40px", marginBottom: "8px" }}>
            👤
          </div>
          <h1 style={styles.title}>Create Your Account</h1>
          <p style={styles.subtitle}>
            Link your Raider Depot token to an account for easier ordering
            and order tracking.
          </p>

          <div style={styles.stepIndicator}>
            <div style={{ ...styles.stepDot, ...styles.stepDotActive }} />
            <div style={styles.stepDot} />
            <div style={styles.stepDot} />
          </div>

          <div style={styles.privacyBanner}>
            🔒 Your account only stores your email and token.
            No names, addresses, or other personal information is collected.
          </div>

          <label style={styles.label}>Your Raider Depot Token</label>
          <input
            style={styles.tokenInput}
            type="text"
            placeholder="RD-RW-001"
            value={token}
            onChange={(e) => {
              setToken(e.target.value.toUpperCase());
              setTokenError("");
            }}
            onKeyDown={(e) => e.key === "Enter" && validateToken()}
            autoComplete="off"
            autoFocus
          />
          <p style={styles.infoText}>
            Enter the token from the QR card you received from your school counselor.
          </p>

          {tokenError && <p style={styles.errorText}>{tokenError}</p>}

          <button
            style={{
              ...styles.buttonPrimary,
              ...(loading ? styles.buttonDisabled : {}),
            }}
            onClick={validateToken}
            disabled={loading}
          >
            {loading ? "Checking..." : "Next: Create Account"}
          </button>

          <div style={{ textAlign: "center", marginTop: "20px" }}>
            <span style={{ fontSize: "13px", color: "#94a3b8" }}>
              Already have an account?{" "}
            </span>
            <a href="/login" style={{ fontSize: "13px", color: "#3b82f6" }}>
              Sign in
            </a>
          </div>

          <div style={{ textAlign: "center", marginTop: "8px" }}>
            <span style={{ fontSize: "13px", color: "#94a3b8" }}>
              Don't want an account?{" "}
            </span>
            <a href="/order" style={{ fontSize: "13px", color: "#3b82f6" }}>
              Order with token only
            </a>
          </div>
        </div>
      </div>
    );
  }

  // ── Render: Step 2 — Email & Password ─────────────────────────────
  if (step === 2) {
    return (
      <div style={styles.container}>
        <NavBar title="Create Account" />
        <div style={styles.card}>
          <h1 style={styles.title}>Create Your Account</h1>
          <p style={styles.subtitle}>
            Set up your email and password for token{" "}
            <strong style={{ color: "#2563eb" }}>{token}</strong>
          </p>

          <div style={styles.stepIndicator}>
            <div style={{ ...styles.stepDot, ...styles.stepDotActive }} />
            <div style={{ ...styles.stepDot, ...styles.stepDotActive }} />
            <div style={styles.stepDot} />
          </div>

          <label style={styles.label}>Email Address</label>
          <input
            style={styles.input}
            type="email"
            placeholder="your.email@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            autoFocus
          />
          <p style={styles.infoText}>
            Used for order confirmations and cycle reminders only.
          </p>

          <label style={styles.label}>Password</label>
          <input
            style={styles.input}
            type="password"
            placeholder="••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
          />
          <p style={styles.passwordReqs}>At least 8 characters</p>

          <label style={styles.label}>Confirm Password</label>
          <input
            style={styles.input}
            type="password"
            placeholder="••••••••"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
          />

          <label style={styles.label}>Number of people in your household</label>
          <select
            style={{ ...styles.input, appearance: "auto" }}
            value={householdSize}
            onChange={(e) => setHouseholdSize(e.target.value)}
          >
            <option value="">Select household size</option>
            {[1,2,3,4,5,6,7,8,9,10,11,12,13,14,15].map(n => (
              <option key={n} value={n}>{n} {n === 1 ? "person" : "people"}</option>
            ))}
          </select>
          <p style={styles.infoText}>
            Include everyone in your household who will benefit from the program.
          </p>

          <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginTop: 20 }}>
            <input
              type="checkbox"
              id="adult-attestation"
              checked={adultAttestation}
              onChange={(e) => setAdultAttestation(e.target.checked)}
              style={{ marginTop: 3, width: 18, height: 18, cursor: "pointer" }}
            />
            <label htmlFor="adult-attestation" style={{ fontSize: 13, color: "#475569", lineHeight: 1.5, cursor: "pointer" }}>
              I confirm that I am 18 years of age or older. I understand that only adults may register an account.
              This program is available to households of Riverwood International Charter School students only.
              One account per household. One order per cycle.
            </label>
          </div>

          {error && <p style={styles.errorText}>{error}</p>}

          <button
            style={{
              ...styles.buttonPrimary,
              ...(loading ? styles.buttonDisabled : {}),
            }}
            onClick={createAccount}
            disabled={loading}
          >
            {loading ? "Creating Account..." : "Create Account"}
          </button>

          <div style={{ textAlign: "center" }}>
            <button
              style={styles.linkButton}
              onClick={() => setStep(1)}
            >
              ← Back to token entry
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Render: Step 3 — Success ──────────────────────────────────────
  return (
    <div style={styles.container}>
      <NavBar title="Create Account" />
      <div style={{ ...styles.card, ...styles.successCard }}>
        <div style={styles.stepIndicator}>
          <div style={{ ...styles.stepDot, ...styles.stepDotActive }} />
          <div style={{ ...styles.stepDot, ...styles.stepDotActive }} />
          <div style={{ ...styles.stepDot, ...styles.stepDotActive }} />
        </div>

        <div style={styles.successIcon}>✅</div>
        <h2 style={{ fontSize: "22px", fontWeight: "700", color: "#16a34a", marginBottom: "8px" }}>
          Account Created!
        </h2>
        <p style={{ color: "#64748b", fontSize: "14px", marginBottom: "8px" }}>
          Your token <strong style={{ color: "#2563eb" }}>{token}</strong> is
          now linked to <strong>{email}</strong>.
        </p>
        <p style={{ color: "#94a3b8", fontSize: "13px", marginBottom: "24px" }}>
          A verification email has been sent. Please check your inbox and your spam or junk folder.
        </p>

        <div style={{ backgroundColor: "#f0fdf4", borderRadius: "10px", padding: "16px", marginBottom: "20px", textAlign: "left" }}>
          <p style={{ fontSize: "14px", color: "#16a34a", fontWeight: "600", margin: "0 0 8px 0" }}>
            What's next:
          </p>
          <p style={{ fontSize: "13px", color: "#475569", margin: "0 0 4px 0" }}>
            • You'll receive email reminders when a new ordering cycle opens
          </p>
          <p style={{ fontSize: "13px", color: "#475569", margin: "0 0 4px 0" }}>
            • Your token will be auto-filled when you sign in to order
          </p>
          <p style={{ fontSize: "13px", color: "#475569", margin: 0 }}>
            • You can view your order history anytime at /my-orders
          </p>
        </div>

        <a
          href="/order"
          style={{
            ...styles.buttonPrimary,
            display: "block",
            textAlign: "center",
            textDecoration: "none",
            boxSizing: "border-box",
          }}
        >
          Place Your First Order →
        </a>
      </div>
    </div>
  );
}
